import { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Button } from '@mui/material';
import { toast } from 'react-toastify';
import ClearIcon from '@mui/icons-material/Clear';

import proposalsServices from '../../../services/proposalsServices';
import equipmentModelsServices from '../../../services/equipmentModelsServices';
import useAutoSave from '../../../hooks/useAutoSave';
import AutoSaveIndicator from '../../AutoSaveIndicator/AutoSaveIndicator';
import SimpleEditor from '../../SimpleEditor';

const ProposalJobOverviewTable = ({ proposal, id, refresh }) => {
  const [jobOverview, setJobOverview] = useState('');
  const [equipmentModelNames, setEquipmentModelNames] = useState([]);
  const validatedRef = useRef(false); // prevent double-validation on load

  // Load equipment model names for validation
  useEffect(() => {
    const loadModels = async () => {
      try {
        const response = await equipmentModelsServices.getAll({ skip: 0, take: 1000000 });
        if (response.data?.data) {
          const names = response.data.data
            .map((m) => m.name?.trim())
            .filter((n) => n && n.length >= 4);
          setEquipmentModelNames(names);
        }
      } catch (error) {
        console.log('Failed to load equipment models for validation:', error);
      }
    };
    loadModels();
  }, []);

  /**
   * Check if text contains equipment model data.
   */
  const isEquipmentDescription = useCallback((text) => {
    if (!text) return false;
    const upper = text.toUpperCase();

    // Check for "MFG:" keyword
    if (/\bMFG\s*[:]/i.test(text)) return true;

    // Check for equipment model numbers from catalog
    for (const modelName of equipmentModelNames) {
      if (upper.includes(modelName.toUpperCase())) return true;
    }

    return false;
  }, [equipmentModelNames]);

  const dirtyRef = useRef(false);
  const lastSavedValueRef = useRef(null);

  // Auto-save function — no refresh() to avoid overwriting active edits
  const saveJobOverview = useCallback(async (data) => {
    await proposalsServices.update(id, {
      jobOverview: data || null,
    });
    lastSavedValueRef.current = data || null;
    dirtyRef.current = false;
  }, [id]);

  // Auto-save on word boundaries (space/period) and on blur — no timer
  const {
    isSaving,
    hasUnsavedChanges,
    lastSaved,
    error,
    markPending,
    saveNow,
    getLastSavedText,
  } = useAutoSave(saveJobOverview);

  // Latest editor value for keyup handlers (state updates are async)
  const latestValueRef = useRef('');

  const saveNowSafe = useCallback((data) => {
    saveNow(data).catch(() => {
      toast.error('Auto-save failed. Your changes will be retried on the next edit.');
    });
  }, [saveNow]);

  useEffect(() => {
    const serverValue = proposal?.jobOverview || '';
    const isExternalChange = serverValue !== (lastSavedValueRef.current || '');
    if (!dirtyRef.current || isExternalChange) {
      setJobOverview(serverValue);
      latestValueRef.current = serverValue;
      validatedRef.current = false;
      dirtyRef.current = false;
    }
  }, [proposal?.jobOverview]);

  // Validate existing content when equipment models are loaded
  useEffect(() => {
    if (equipmentModelNames.length === 0 || !jobOverview || validatedRef.current) return;
    validatedRef.current = true;

    if (isEquipmentDescription(jobOverview)) {
      toast.error(
        '⚠️ Equipment model numbers detected in Job Overview. Job Overview has been cleared. Use the Equipment section to add equipment.',
        { autoClose: 8000 }
      );
      setJobOverview('');
      latestValueRef.current = '';
      saveJobOverview(null);
    }
  }, [equipmentModelNames, isEquipmentDescription]);

  const handleChange = (e) => {
    const newValue = e.target.value;
    dirtyRef.current = true;

    const plainText = newValue.replace(/<[^>]*>/g, '');
    if (isEquipmentDescription(plainText)) {
      toast.error(
        '⚠️ Equipment model numbers must not be entered in Job Overview. Use the Equipment section. Job Overview has been cleared.',
        { autoClose: 8000 }
      );
      setJobOverview('');
      latestValueRef.current = '';
      saveNowSafe('');
      return;
    }

    setJobOverview(newValue);
    latestValueRef.current = newValue;
    markPending(newValue);
  };

  // Word boundary: space or period commits the typed text immediately
  const handleKeyUp = (e) => {
    if (e.key === ' ' || e.key === '.') {
      saveNowSafe(latestValueRef.current);
    }
  };

  // Flush pending text when leaving the editor (last word has no boundary)
  const handleBlur = () => {
    saveNowSafe();
  };

  const handleClear = async () => {
    setJobOverview('');
    dirtyRef.current = false;
    lastSavedValueRef.current = null;
    try {
      await proposalsServices.update(id, { jobOverview: null });
      if (refresh) refresh();
    } catch (err) {
      console.error('Clear error:', err);
    }
  };

  return (
    <Box>
      <SimpleEditor
        value={jobOverview}
        onChange={handleChange}
        onKeyUp={handleKeyUp}
        onBlur={handleBlur}
        minHeight={140}
        placeholder="Enter a description of the work to be performed..."
        renderHeader={(toolbar) => (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ fontSize: 14, color: '#666', whiteSpace: 'nowrap' }}>Job Overview</Box>
              {toolbar}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {jobOverview && (
                <Button
                  size="small"
                  color="error"
                  startIcon={<ClearIcon />}
                  onClick={handleClear}
                  disabled={isSaving}
                >
                  Clear
                </Button>
              )}
              <AutoSaveIndicator
                isSaving={isSaving}
                hasUnsavedChanges={hasUnsavedChanges}
                lastSaved={lastSaved}
                error={error}
                getLastSavedText={getLastSavedText}
                variant="chip"
              />
            </Box>
          </Box>
        )}
      />
    </Box>
  );
};

export default ProposalJobOverviewTable;
